import { apolloClient } from '~/apollo-client';
import { ParsedQueryParams, UnparsedQueryParams } from '~/providers/query-params/types';
import { AddressType } from '~/typings/context';
import {
  CustomerAccountType,
  EnrollingUserInput,
  Flex_CreateEnrollingUserMutation,
  Flex_CreateEnrollingUserMutationVariables,
  OriginationSource,
} from '~/typings/graphql';
import { VALID_SIGNNOW_URLS } from '~/utils/constants';
import { getDmiClickId } from '~/utils/dmi-pixel';
import { errors } from '~/utils/error-messages';
import { determineOriginationSource, determineLanguage } from '~/utils/funnels';
import { determineProjectGroup } from '~/utils/project-groups';
import { store } from '~/utils/store';
import { sendToSegment } from '~/utils/tracking';
import { CREATE_ENROLLING_USER } from './create-user.queries';

export const createUser = async (params: CreateUserParams): Promise<CreateUserResponse> => {
  const processedUnparsedQueryParams = processUnparsedQueryParams(
    params.unparsedQueryParams || {},
    params.email,
  );
  const input: EnrollingUserInput = {
    budgetBilling: !!params.budgetBilling,
    businessName: params.businessName,
    businessPhone: params.businessPhone,
    contractVersionIds: params.generalContractIds,
    communitySolarProjectGroupId: determineProjectGroup(
      params.utilityId,
      params.funnelId,
      params.projectSnapshotData,
    ),
    customerType:
      params.businessName || params.customerType === CustomerAccountType.Business
        ? 'smb'
        : undefined,
    dateOfBirth: params.dateOfBirth,
    email: params.email,
    firstName: params.firstName,
    funnel: params.funnel || params.funnelId,
    inviteCode: params.inviteCode ? String(params.inviteCode) : undefined,
    ipAddress: params.ipAddress,
    jobTitle: params.jobTitle,
    languageShortCode: determineLanguage(),
    lastName: params.lastName,
    funnelVnmProjectId: params.funnelVnmProjectId,
    originationSourceType: params.originationSource || determineOriginationSource(params),
    password: params.password,
    phone: params.phone,
    smsOptIn: !!params.smsOptIn,
    stripeCustomerId: params.stripeCustomerId,
    stripePaymentId: params.stripePaymentId,
    planTemplateId: params.planTemplateId,
    contactAddressCity: params.address?.city,
    contactAddressState: params.address?.state,
    contactAddressStreetOne: params.address?.street,
    contactAddressZipCode: params.address?.zipCode,
    queryParams: processedUnparsedQueryParams,
    signupContractTemplateId: params.signupContractTemplateId,
    referrableCode: params.promoCode,
    utilityId: params.utilityId,
    zip: params.zipCode,
    metaData: params.metaData,
    ...(params.communicationPreferences
      ? { communicationPreferences: params.communicationPreferences }
      : {}),
  };

  if (params.addressSecondary) {
    input.contactAddressStreetTwo = params.addressSecondary;
  }
  if (params.zohoReferenceNumber) {
    input.zohoReferenceNumber = params.zohoReferenceNumber;
  }
  if (params.dealerCode) {
    input.dealerCode = params.dealerCode;
  }
  if (params.countyId) {
    input.countyId = params.countyId;
  }
  const clickId = getDmiClickId() ?? params.clickId;
  if (clickId) {
    input.clickId = clickId;
    input.queryParams = {
      ...processedUnparsedQueryParams,
      clickId,
    };
  }
  const response = await apolloClient.mutate<
    Flex_CreateEnrollingUserMutation,
    Flex_CreateEnrollingUserMutationVariables
  >({
    mutation: CREATE_ENROLLING_USER,
    variables: { input },
  });

  const userId = response.data?.enrollingUser.user?.id;
  const signupFunnelLeadId = response.data?.enrollingUser.user?.signupFunnelLeadId || undefined;
  if (!response.data || !userId) throw new Error(errors.missingCreateAccountResponse);

  store.set('TOKEN', response.data.enrollingUser.token!);
  store.set('USER_ID', userId);
  store.set('SIGNUP_FUNNEL_LEAD_ID', signupFunnelLeadId);

  return { userId, signupFunnelLeadId };
};

export interface CreateUserParams {
  budgetBilling?: boolean;
  businessName?: string;
  communitySolarProjectGroupId?: number;
  customerType?: string;
  email: string;
  firstName: string;
  funnelId?: string;
  funnelVnmProjectId?: number;
  generalContractIds?: number[];
  inviteCode?: string;
  ipAddress?: string;
  lastName: string;
  address?: AddressType;
  addressSecondary?: string;
  originationSource?: OriginationSource;
  password?: string;
  communicationPreferences?: string[];
  phone?: string;
  smsOptIn?: boolean;
  planTemplateId: number;
  promoCode?: string;
  stripeCustomerId?: string;
  stripePaymentId?: string;
  unparsedQueryParams: ParsedQueryParams['unparsedQueryParams'];
  utilityId: number;
  funnelTrack?: string;
  funnel?: string;
  utmParams?: Record<string, string | number>;
  zipCode: string;
  metaData?: string;
  dateOfBirth?: string;
  jobTitle?: string;
  businessPhone?: string;
  projectSnapshotData?: { programName?: string | null } | null;
  signupContractTemplateId?: number;
  zohoReferenceNumber?: number;
  dealerCode?: string;
  clickId?: string;
  countyId?: number;
}

export interface CreateUserResponse {
  userId: number;
  signupFunnelLeadId?: number;
}
export const processUnparsedQueryParams = (
  unparsedQueryParams: UnparsedQueryParams,
  email: string,
): {
  utilityAccountNumber?: string;
  secondaryAccountId?: string;
  esign_mdu?: string;
  esign_pg?: string;
  esign_sa?: string;
  perchSfProductId?: string;
} => {
  // Do not change the decodingMap
  const decodeStringtoNumber = (
    encodedNumber: string,
    email: string,
    queryParam: string,
  ): string | undefined => {
    const decodingMap: Record<string, string> = {
      r: '0',
      m: '1',
      b: '2',
      j: '3',
      p: '4',
      t: '5',
      c: '6',
      g: '7',
      f: '8',
      w: '9',
    };

    let decodedNumber = '';
    let letter;

    for (letter of encodedNumber) {
      if (decodingMap.hasOwnProperty(letter)) {
        decodedNumber += decodingMap[letter];
      } else {
        sendToSegment({
          type: 'track_queryParams_decoding_error',
          encodedNumber,
          queryParam,
          email,
        });
        return undefined;
      }
    }
    return decodedNumber;
  };

  const utilityAccountNumber = unparsedQueryParams.encodedUtilityAccountNumber
    ? decodeStringtoNumber(
        unparsedQueryParams.encodedUtilityAccountNumber,
        email,
        'utilityAccountNumber',
      )
    : undefined;

  const secondaryAccountId = unparsedQueryParams.encodedSecondaryAccountId
    ? decodeStringtoNumber(
        unparsedQueryParams.encodedSecondaryAccountId,
        email,
        'secondaryAccountId',
      )
    : undefined;

  const { perchSfProductId } = unparsedQueryParams;
  /* eslint-disable camelcase */
  const esign_mdu = validateSignNowUrl('esign_mdu', unparsedQueryParams.esign_mdu);
  const esign_pg = validateSignNowUrl('esign_pg', unparsedQueryParams.esign_pg);
  const esign_sa = validateSignNowUrl('esign_sa', unparsedQueryParams.esign_sa);

  return {
    ...unparsedQueryParams,
    utilityAccountNumber,
    secondaryAccountId,
    esign_mdu,
    esign_pg,
    esign_sa,
    perchSfProductId,
  };
  /* eslint-enable camelcase */
};

const validateSignNowUrl = (contract: string, url: string | undefined): string | undefined => {
  if (!url) return undefined;
  try {
    const urlObj = new URL(url);
    if (!VALID_SIGNNOW_URLS.includes(urlObj.origin)) {
      sendToSegment({
        type: 'invalid_signnow_url',
        contractUrl: url,
        contract,
        error: 'Provided URL is not a valid SignNow URL',
      });
      return undefined;
    }
    return urlObj.toString();
  } catch (error) {
    sendToSegment({
      type: 'invalid_signnow_url',
      contractUrl: url,
      contract,
      error: error instanceof Error ? error.message : String(error),
    });
    return undefined;
  }
};
